import { useState } from "react";
import { useTranslation } from "react-i18next";

import InputGlobal from "shared/components/Form/InputGlobal";
import { SuccessUpdatePasswordModal } from "shared/components/Modals/SuccessUpdatePassword";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import { ERROR_STATUS_UNPROCESSABLE } from "shared/constants";

import { validatePassword } from "shared/helpers/validation";
import { getParameterByName } from "shared/helpers/view";

import { useCurrentCountry } from "shared/hooks/useCurrentCountry";

import { updatePassword } from "shared/services/auth";

import { EmptyToken } from "./EmptyToken.jsx";

import { ReactComponent as EyeIcon } from "assets/shared/img/icons/eye.svg";
import { ReactComponent as EyeIconHide } from "assets/shared/img/icons/eye-crossed.svg";

export const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState({
    token: getParameterByName("token", window.location.query),
  });
  const [errorPassword, setErrorPassword] = useState(null);
  const [errorRepeatPassword, setRepeatErrorPassword] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [loader, setLoader] = useState(false);
  const [successUpdatePassword, setSuccessUpdatePassword] = useState(false);
  const [emptyToken, setEmptyToken] = useState(false);
  const { t } = useTranslation("updatePassword");

  useCurrentCountry();

  /*
   * Toggle for show Password on click icon eye
   * */
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  /*
   * Toggle for show repeat Password on click icon eye
   * */
  const toggleShowPasswordRepeat = () => {
    setShowPasswordRepeat(!showPasswordRepeat);
  };

  /**
   * Handler for change Password
   * @param {object} event
   */
  const handleChangePassword = (event) => {
    setErrorPassword(null);
    setNewPassword({ ...newPassword, password: event?.target?.value });
  };

  /**
   * Handler for change repeat Password
   * @param {object} event
   */
  const handleChangeRepeatPassword = (event) => {
    setRepeatErrorPassword(null);
    setNewPassword({ ...newPassword, repeat_password: event?.target?.value });
  };

  /**
   * Close Success Modal
   */
  const closeModalUpdatePassword = () => {
    setSuccessUpdatePassword(false);
    window.location.href = "/app/";
  };

  /**
   * Validation form and post request on Api
   */
  const handleSubmitResetPassword = (e) => {
    e.preventDefault();

    let isSuccessValidation = true;
    const resultPassword = validatePassword(
      newPassword.password,
      newPassword.repeat_password
    );

    if (!resultPassword.status) {
      setErrorPassword(resultPassword.errorPassword?.text);
      setRepeatErrorPassword(resultPassword.errorRepeatPass?.text);
      isSuccessValidation = false;
    }

    if (isSuccessValidation) {
      if (newPassword.token === "") {
        setEmptyToken(true);
        return;
      }
      setLoader(true);
      updatePassword(newPassword)
        .then(({ data }) => {
          setLoader(false);
          if (data.success) {
            setSuccessUpdatePassword(true);
            setNewPassword({});
          }
        })
        .catch(({ response }) => {
          setLoader(false);
          if (response.status === ERROR_STATUS_UNPROCESSABLE) {
            const { data } = response;
            let errorText = "";
            data.errors.forEach((error) => {
              if (error.name === "password") {
                setErrorPassword(error.message);
              } else if (error.name === "repeat_password") {
                setRepeatErrorPassword(error.message);
              } else {
                errorText +=
                  errorText.length > 0 ? " " + error.message : error.message;
              }
            });
          } else {
            setResponseError(t("error"));
          }
        });
    }
  };

  return (
    <>
      {emptyToken ? (
        <EmptyToken />
      ) : (
        <div className="upd-password">
          <div className="upd-pass__wrap">
            <div className="upd-pass_header">
              <span className="title">{t("updateTitle")}</span>
            </div>
            <form noValidate onSubmit={handleSubmitResetPassword}>
              <div className="reset_pass-body">
                {loader && (
                  <SpinLoader
                    color={LOADER_COLOR_DARK}
                    size={LOADER_SIZE_LARGE}
                    position={POSITION_ABSOLUTE}
                    withBlur={true}
                  />
                )}
                <span className="label">{t("newPassword")}</span>
                <InputGlobal
                  className={"update__password"}
                  handleChange={(val) => handleChangePassword(val)}
                  name="update-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword.password}
                  error={!!errorPassword}
                  errorText={errorPassword}
                  placeholder={t("newPassword")}
                  rightIconSrc={showPassword ? <EyeIcon /> : <EyeIconHide />}
                  handleClickRightIcon={toggleShowPassword}
                  passwordShowIcon={showPassword}
                />
                <span className="label">{t("repeatPassword")}</span>
                <InputGlobal
                  className={"update__password"}
                  handleChange={(val) => handleChangeRepeatPassword(val)}
                  name="repeat-update-password"
                  type={showPasswordRepeat ? "text" : "password"}
                  value={newPassword.repeat_password}
                  error={!!errorRepeatPassword}
                  errorText={errorRepeatPassword}
                  placeholder={t("repeatPassword")}
                  rightIconSrc={
                    showPasswordRepeat ? <EyeIcon /> : <EyeIconHide />
                  }
                  handleClickRightIcon={toggleShowPasswordRepeat}
                  passwordShowIcon={showPasswordRepeat}
                />
                <button
                  onClick={handleSubmitResetPassword}
                  className="btn_update"
                >
                  {t("button")}
                </button>
                {responseError && (
                  <p className="response__error">{responseError}</p>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {successUpdatePassword && (
        <SuccessUpdatePasswordModal
          show={successUpdatePassword}
          close={closeModalUpdatePassword}
        />
      )}
    </>
  );
};
